import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  // v1.10.102: business_name is optional for personal customers;
  // contact_name is the required identity field so we've got something
  // to chase them by.
  { key: "business_name", label: "Business name (if applicable)", placeholder: "Acme Pty Ltd — leave blank for personal customers" },
  { key: "contact_name", label: "Your name", required: true, placeholder: "Jane Doe" },
  { key: "email", label: "Email", type: "email", required: true, placeholder: "jane@acme.com.au" },
  { key: "phone", label: "Phone", placeholder: "0400 000 000" },
  { key: "service_address", label: "Service address", placeholder: "123 Example St, Suburb, State 0000", full: true },
  { key: "abn", label: "ABN (if applicable)" },
  { key: "current_service", label: "Current internet plan" },
  { key: "chosen_plan", label: "Plan you'd like to move to", placeholder: "e.g. NBN 100/40" },
  { key: "notes", label: "Anything else we should know?", multiline: true, full: true },
];

const inputCls =
  "w-full rounded-lg border border-slate-800 bg-slate-950 text-slate-100 placeholder:text-slate-600 px-4 py-3 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-colors";

export default function PublicSignup() {
  // Partner-attributed signup — never index these; they must only be
  // reachable via a link the partner hands out directly. See
  // ``InternetSignup.jsx`` for the sibling implementation.
  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow, noarchive, nosnippet";
    tag.setAttribute("data-signup-noindex", "1");
    document.head.appendChild(tag);
    return () => {
      const el = document.head.querySelector('meta[data-signup-noindex="1"]');
      if (el) el.remove();
    };
  }, []);
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/public/signup/${slug}`)
      .then(r => setMeta(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  async function submit(e) {
    e.preventDefault();
    // v1.10.102: personal customers may not have a business name —
    // gate on contact_name + email instead so we've always got a
    // human to reach.
    if (!form.contact_name?.trim()) {
      toast.error("Your name is required");
      return;
    }
    if (!form.email?.trim()) {
      toast.error("Email is required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/public/signup/${slug}`, form);
      setDone(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Submission failed. Please try again in a moment.");
    } finally { setBusy(false); }
  }

  if (notFound) return <NotFound />;
  if (!meta) return (
    <div className="min-h-screen bg-slate-950 grid place-items-center">
      <Loader2 className="w-8 h-8 text-cyan-500 animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <div className="mb-10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-400 font-semibold">
            {meta.brand_name} · {meta.partner_name}
          </div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3 leading-tight">
            Let&apos;s get you switched over.
          </h1>
          <p className="text-slate-400 mt-3 max-w-xl">
            Tell us a bit about you and your current internet setup. We&apos;ll route this straight to {meta.partner_name}&apos;s team at {meta.brand_name} — they&apos;ll be in touch within a business day.
          </p>
        </div>

        {done ? (
          <SuccessCard brand={meta.brand_name} partner={meta.partner_name} />
        ) : (
          <form onSubmit={submit} className="rounded-2xl border border-slate-800 bg-slate-900 p-6 md:p-8" data-testid="public-signup-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {FIELDS.map(f => (
                <label key={f.key} className={`block ${f.full ? "md:col-span-2" : ""}`}>
                  <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                    {f.label} {f.required && <span className="text-red-400">*</span>}
                  </span>
                  {f.multiline ? (
                    <textarea
                      data-testid={`public-field-${f.key}`}
                      rows={4} value={form[f.key] || ""} placeholder={f.placeholder || ""}
                      onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                      className={inputCls}
                    />
                  ) : (
                    <input
                      data-testid={`public-field-${f.key}`}
                      type={f.type || "text"} required={f.required}
                      value={form[f.key] || ""} placeholder={f.placeholder || ""}
                      onChange={(e) => setForm({...form, [f.key]: e.target.value})}
                      className={inputCls}
                    />
                  )}
                </label>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
              <p className="text-xs text-slate-500 max-w-md">
                By submitting, you agree to be contacted by {meta.partner_name} and {meta.brand_name} about your switch-over. We don&apos;t share this information anywhere else.
              </p>
              <button
                data-testid="public-signup-submit"
                type="submit" disabled={busy}
                className="inline-flex items-center gap-2 bg-brand-gradient text-slate-950 px-6 py-3 rounded-full font-semibold text-sm hover:brightness-110 hover:-translate-y-[1px] transition-all disabled:opacity-60"
              >
                {busy ? "Submitting…" : <>Submit signup <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function SuccessCard({ brand, partner }) {
  return (
    <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center" data-testid="public-signup-success">
      <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
      <h2 className="font-display text-2xl tracking-tight mb-2">Thanks — we&apos;ve got it.</h2>
      <p className="text-slate-300 max-w-md mx-auto">
        {partner}&apos;s team at {brand} will be in touch within a business day to walk you through the switch-over.
      </p>
    </div>
  );
}

function NotFound() {
  return (
    <div className="min-h-screen bg-slate-950 grid place-items-center px-6">
      <div className="text-center max-w-md">
        <h1 className="font-display text-3xl tracking-tight mb-2">Signup page not found</h1>
        <p className="text-slate-400 mb-6">This partner signup link is no longer active. Check the URL or reach out to your account manager.</p>
        <Link to="/" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
      </div>
    </div>
  );
}
